import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiCall } from '../utils/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for stored authentication token
    const token = localStorage.getItem('admin_token');
    const userData = localStorage.getItem('admin_user');
    
    if (token && userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
      }
    }
    
    setLoading(false);
  }, []);

  // Shared by both the admin/CRM login and the facility login - both hit the
  // same real backend endpoint. Each caller decides what role it requires
  // (see AdminLoginPage / FacilityLoginPage) since a single authenticated
  // session can be either an admin or a facility account.
  const login = async (email, password) => {
    try {
      const result = await apiCall('/api/facility/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });

      const userData = {
        id: result.user_id,
        email,
        role: result.role,
      };

      localStorage.setItem('admin_token', result.access_token);
      localStorage.setItem('admin_user', JSON.stringify(userData));

      setUser(userData);
      return { success: true, user: userData };
    } catch (error) {
      if (error.message?.includes('429')) {
        return { success: false, error: 'Too many failed login attempts. Please try again in a few minutes.' };
      }
      return { success: false, error: 'Invalid email or password' };
    }
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    setUser(null);
  };

  const isAuthenticated = () => {
    return !!user && !!localStorage.getItem('admin_token');
  };

  const value = {
    user,
    login,
    logout,
    isAuthenticated,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};